import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Button, Typography, CircularProgress, Alert, Link, List, ListItem, ListItemText, Paper, FormControl, InputLabel, Select, MenuItem, LinearProgress, Chip, FormControlLabel, Switch } from '@mui/material';
import { CloudUpload as CloudUploadIcon, LocationOn as LocationOnIcon, CheckCircle as CheckCircleIcon, Error as ErrorIcon, HourglassEmpty as PendingIcon } from '@mui/icons-material';
import { useApi } from '../hooks/useApi';
import { useSearch } from '../contexts/SearchContext';

interface FileUploadStatus {
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
  jobId?: string;
  splitCount?: number;
}

interface UploadSummary {
  total: number;
  success: number;
  failed: number;
  totalSplits: number;
}

const PdfUploader: React.FC = () => {
  const [files, setFiles] = useState<FileUploadStatus[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadLocation, setUploadLocation] = useState<string>('');
  const [useAbuDhabiOnshore, setUseAbuDhabiOnshore] = useState<boolean>(false);
  const [summary, setSummary] = useState<UploadSummary | null>(null);
  const { callApi } = useApi();
  const { availableLocations, selectedLocation, locationLoading } = useSearch();

  // Set default upload location when available locations change
  useEffect(() => {
    if (availableLocations.length > 0 && !uploadLocation) {
      if (availableLocations.length === 1) {
        setUploadLocation(availableLocations[0]);
      } else if (selectedLocation) {
        setUploadLocation(selectedLocation);
      } else {
        setUploadLocation(availableLocations[0]);
      }
    }
  }, [availableLocations, selectedLocation, uploadLocation]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const newFiles: FileUploadStatus[] = acceptedFiles.map(file => ({
        file,
        status: 'pending' as const
      }));
      setFiles(prev => [...prev, ...newFiles]);
      setError(null);
      setSummary(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: true,
    disabled: uploading
  });

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const clearAll = () => {
    setFiles([]);
    setSummary(null);
    setError(null);
  };

  const uploadFiles = async () => {
    if (files.length === 0) return;

    if (!uploadLocation) {
      setError('Please select a location before uploading');
      return;
    }

    setUploading(true);
    setError(null);
    setSummary(null);

    const results: UploadSummary = {
      total: files.length,
      success: 0,
      failed: 0,
      totalSplits: 0
    };

    // Process files sequentially to avoid overwhelming the server
    for (let i = 0; i < files.length; i++) {
      const fileStatus = files[i];

      // Update status to uploading
      setFiles(prev => prev.map((f, idx) =>
        idx === i ? { ...f, status: 'uploading' as const } : f
      ));

      const formData = new FormData();
      formData.append('file', fileStatus.file);
      formData.append('location', uploadLocation);
      if (uploadLocation === 'AbuDhabi' && useAbuDhabiOnshore) {
        formData.append('parser', 'gemini');
      }

      try {
        const response = await callApi('/api/pdf/split', {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || `Upload failed: ${response.statusText}`);
        }

        const data = await response.json();

        // Update status to success
        setFiles(prev => prev.map((f, idx) =>
          idx === i ? {
            ...f,
            status: 'success' as const,
            jobId: data.jobId,
            splitCount: data.splitUrls?.length || data.urls?.length || 0
          } : f
        ));

        results.success++;
        results.totalSplits += data.splitUrls?.length || data.urls?.length || 0;

      } catch (err) {
        // Update status to error
        setFiles(prev => prev.map((f, idx) =>
          idx === i ? {
            ...f,
            status: 'error' as const,
            error: err instanceof Error ? err.message : 'Upload failed'
          } : f
        ));

        results.failed++;
        console.error(`Upload error for ${fileStatus.file.name}:`, err);
      }
    }

    setSummary(results);
    setUploading(false);
  };

  const pendingCount = files.filter(f => f.status === 'pending').length;
  const uploadingCount = files.filter(f => f.status === 'uploading').length;
  const successCount = files.filter(f => f.status === 'success').length;
  const errorCount = files.filter(f => f.status === 'error').length;
  const progress = files.length > 0 ? ((successCount + errorCount) / files.length) * 100 : 0;

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', p: 3 }}>
      {/* Location Selection - only show if multiple locations available */}
      {availableLocations.length > 1 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <FormControl fullWidth>
            <InputLabel id="upload-location-label">Upload Location</InputLabel>
            <Select
              labelId="upload-location-label"
              value={uploadLocation}
              label="Upload Location"
              onChange={(event) => setUploadLocation(event.target.value)}
              startAdornment={<LocationOnIcon sx={{ mr: 1 }} />}
              disabled={locationLoading || uploading}
            >
              {availableLocations.map((location) => (
                <MenuItem key={location} value={location}>
                  {location}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {uploadLocation && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Documents will be uploaded to: <strong>{uploadLocation}</strong>
            </Typography>
          )}
        </Paper>
      )}

      {/* Show current location if only one available */}
      {availableLocations.length === 1 && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'action.hover' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationOnIcon color="primary" />
            <Typography variant="body1">
              Upload Location: <strong>{availableLocations[0]}</strong>
            </Typography>
          </Box>
        </Paper>
      )}

      {/* AbuDhabi-only: onshore parser toggle (ADNOC Onshore) */}
      {uploadLocation === 'AbuDhabi' && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={useAbuDhabiOnshore}
                onChange={(e) => setUseAbuDhabiOnshore(e.target.checked)}
                disabled={uploading}
              />
            }
            label="Use Onshore parser (ADNOC Onshore reports)"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {useAbuDhabiOnshore
              ? 'ADNOC Onshore reports will be processed via the detailed Gemini onshore extractor.'
              : 'Reports will be processed via the default Abu Dhabi extractor.'}
          </Typography>
        </Paper>
      )}

      <Paper
        {...getRootProps()}
        sx={{
          p: 3,
          textAlign: 'center',
          cursor: uploading ? 'not-allowed' : 'pointer',
          bgcolor: isDragActive ? 'action.hover' : 'background.paper',
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'divider',
          mb: 3,
          opacity: uploading ? 0.6 : 1
        }}
      >
        <input {...getInputProps()} />
        <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          {isDragActive ? 'Drop the PDFs here' : 'Drag and drop PDF files here, or click to select'}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          You can select multiple PDF files at once
        </Typography>
      </Paper>

      {/* File List */}
      {files.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Selected Files ({files.length})
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {pendingCount > 0 && <Chip label={`${pendingCount} pending`} size="small" color="default" />}
              {uploadingCount > 0 && <Chip label={`${uploadingCount} uploading`} size="small" color="info" />}
              {successCount > 0 && <Chip label={`${successCount} success`} size="small" color="success" />}
              {errorCount > 0 && <Chip label={`${errorCount} failed`} size="small" color="error" />}
            </Box>
          </Box>

          {uploading && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress variant="determinate" value={progress} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Processing {successCount + errorCount + 1} of {files.length}...
              </Typography>
            </Box>
          )}

          <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
            {files.map((fileStatus, index) => (
              <ListItem
                key={index}
                secondaryAction={
                  fileStatus.status === 'pending' && !uploading ? (
                    <Button size="small" onClick={() => removeFile(index)} color="error">
                      Remove
                    </Button>
                  ) : null
                }
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  {fileStatus.status === 'pending' && <PendingIcon color="disabled" fontSize="small" />}
                  {fileStatus.status === 'uploading' && <CircularProgress size={16} />}
                  {fileStatus.status === 'success' && <CheckCircleIcon color="success" fontSize="small" />}
                  {fileStatus.status === 'error' && <ErrorIcon color="error" fontSize="small" />}

                  <ListItemText
                    primary={fileStatus.file.name}
                    secondary={
                      fileStatus.status === 'error'
                        ? fileStatus.error
                        : fileStatus.status === 'success' && fileStatus.splitCount
                          ? `Split into ${fileStatus.splitCount} files`
                          : `${(fileStatus.file.size / 1024 / 1024).toFixed(2)} MB`
                    }
                    secondaryTypographyProps={{
                      color: fileStatus.status === 'error' ? 'error' : 'text.secondary'
                    }}
                  />
                </Box>
              </ListItem>
            ))}
          </List>

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button
              variant="contained"
              onClick={uploadFiles}
              disabled={uploading || pendingCount === 0 || !uploadLocation}
              startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
            >
              {uploading ? `Uploading... (${successCount + errorCount}/${files.length})` : `Upload ${pendingCount} File${pendingCount !== 1 ? 's' : ''}`}
            </Button>
            <Button
              variant="outlined"
              onClick={clearAll}
              disabled={uploading}
            >
              Clear All
            </Button>
          </Box>
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Upload Summary */}
      {summary && (
        <Alert
          severity={summary.failed === 0 ? 'success' : summary.success === 0 ? 'error' : 'warning'}
          sx={{ mb: 3 }}
        >
          <Typography variant="subtitle1" gutterBottom>
            <strong>Upload Complete!</strong>
          </Typography>
          <Typography variant="body2">
            • Total files: {summary.total}<br />
            • Successfully uploaded: {summary.success}<br />
            • Failed: {summary.failed}<br />
            • Total split PDFs created: {summary.totalSplits}
          </Typography>
          {summary.success > 0 && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Go to "Status" to see the processing progress of individual files.
            </Typography>
          )}
        </Alert>
      )}
    </Box>
  );
};

export default PdfUploader;
